/**
 * VariationsView.tsx — comparaison des directions de layout d'éditeur.
 * Porté (version informative) depuis snapbooth-tempalte/project/variations.jsx.
 *
 * Le bundle original utilisait un design-canvas pan/zoom avec des MiniEditor.
 * Ici on présente les 3 directions sous forme de cards avec aperçu schématique —
 * c'est un artefact de décision design, pas une feature utilisateur lourde.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check, ArrowRight } from 'lucide-react';

interface LayoutVariant {
  id: string;
  label: string;
  tagline: string;
  meta: string;
  audience: string;
  recommended?: boolean;
  /** Schéma de répartition des panneaux (grille de blocs colorés). */
  schema: { name: string; flex: number; tone: string }[];
}

const VARIANTS: LayoutVariant[] = [
  {
    id: 'v1',
    label: 'V1 — Canva',
    tagline: 'Par défaut · grand public',
    meta: 'Sidebar grasse · Drawer · Canvas · Properties · Bottombar',
    audience: 'Recommandé pour les utilisateurs occasionnels',
    recommended: true,
    schema: [
      { name: 'Sidebar', flex: 1, tone: 'var(--accent-200)' },
      { name: 'Drawer', flex: 2, tone: 'var(--accent-100)' },
      { name: 'Canvas', flex: 5, tone: 'var(--bg-canvas)' },
      { name: 'Props', flex: 2, tone: 'var(--accent-100)' },
    ],
  },
  {
    id: 'v2',
    label: 'V2 — Figma Pro',
    tagline: 'Dense · professionnels',
    meta: 'Sidebar slim · Panneau droit unifié (templates + propriétés)',
    audience: "Pour les pros qui veulent un maximum d'espace canvas",
    schema: [
      { name: 'Slim', flex: 0.6, tone: 'var(--accent-200)' },
      { name: 'Canvas', flex: 6, tone: 'var(--bg-canvas)' },
      { name: 'Panneau unifié', flex: 3, tone: 'var(--accent-100)' },
    ],
  },
  {
    id: 'v3',
    label: 'V3 — Top toolbar',
    tagline: 'Immersif · canvas plein',
    meta: 'Toolbar horizontale en haut · Bibliothèque en dock latéral',
    audience: 'Pour une expérience de dessin immersive',
    schema: [{ name: 'Toolbar (haut)', flex: 10, tone: 'var(--accent-200)' }],
  },
];

const CONTROL_STYLES = [
  {
    id: 'soft',
    name: 'Soft pills',
    desc: 'Coins arrondis, fonds doux — chaleureux',
    radius: '999px',
    filled: true,
  },
  {
    id: 'outlined',
    name: 'Outlined',
    desc: 'Bordures fines, fond transparent — sobre',
    radius: '8px',
    filled: false,
  },
  {
    id: 'flat',
    name: 'Flat',
    desc: 'Sans bordure, fond plein au survol — minimal',
    radius: '6px',
    filled: true,
  },
];

export const VariationsView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="sb-vars-shell">
      <div className="sb-vars-content">
        <header className="sb-vars-intro">
          <div className="sb-dash-chip" style={{ marginBottom: 12 }}>
            <Sparkles size={12} /> 3 directions à comparer
          </div>
          <h1 className="sb-vars-h1">Trois layouts d'éditeur</h1>
          <p className="sb-vars-sub">
            Trois manières de placer les panneaux. La V1 est le prototype actif (utilisé par l'app),
            V2 et V3 montrent comment l'éditeur pourrait se réorganiser pour différents profils.
          </p>
        </header>

        <section className="sb-vars-grid">
          {VARIANTS.map(v => (
            <article key={v.id} className={`sb-vars-card ${v.recommended ? 'is-recommended' : ''}`}>
              {v.recommended && (
                <div className="sb-vars-badge">
                  <Check size={11} /> Direction retenue
                </div>
              )}
              <div className="sb-vars-card-head">
                <h2 className="sb-vars-card-title">{v.label}</h2>
                <span className="sb-vars-card-tag">{v.tagline}</span>
              </div>

              {/* Schéma de répartition des panneaux */}
              <div className={`sb-vars-schema ${v.id === 'v3' ? 'is-vertical' : ''}`}>
                {v.schema.map((block, i) => (
                  <div
                    key={i}
                    className="sb-vars-block"
                    style={{ flex: block.flex, background: block.tone }}
                  >
                    <span>{block.name}</span>
                  </div>
                ))}
                {v.id === 'v3' && (
                  <div
                    className="sb-vars-block"
                    style={{ flex: 8, background: 'var(--bg-canvas)' }}
                  >
                    <span>Canvas immersif</span>
                  </div>
                )}
              </div>

              <p className="sb-vars-card-meta">{v.meta}</p>
              <p className="sb-vars-card-audience">{v.audience}</p>

              {v.recommended && (
                <button
                  className="sb-btn sb-btn-primary"
                  style={{ width: '100%', marginTop: 12 }}
                  onClick={() => navigate('/editor')}
                >
                  Ouvrir l'éditeur <ArrowRight size={14} />
                </button>
              )}
            </article>
          ))}
        </section>

        <header className="sb-vars-intro" style={{ marginTop: 40 }}>
          <h2 className="sb-vars-h2">Style des contrôles</h2>
          <p className="sb-vars-sub">Trois approches pour les boutons, sliders et inputs.</p>
        </header>

        <section className="sb-vars-controls">
          {CONTROL_STYLES.map(c => (
            <article key={c.id} className="sb-vars-ctl-card">
              <div className="sb-vars-ctl-title">{c.name}</div>
              <div className="sb-vars-ctl-desc">{c.desc}</div>
              <div className="sb-vars-ctl-demo">
                <button
                  style={{
                    borderRadius: c.radius,
                    background: c.filled ? 'var(--accent-500)' : 'transparent',
                    color: c.filled ? 'white' : 'var(--accent-600)',
                    border: c.filled ? 'none' : '1px solid var(--accent-500)',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Bouton
                </button>
                <span
                  style={{
                    borderRadius: c.radius,
                    background: c.filled ? 'var(--bg-chip)' : 'transparent',
                    border: c.filled ? '1px solid transparent' : '1px solid var(--border-default)',
                    padding: '8px 12px',
                    fontSize: '13px',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  Input
                </span>
              </div>
            </article>
          ))}
        </section>

        <div style={{ height: 60 }} />
      </div>
    </div>
  );
};
